
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { BLOGS, MAIN_COUPON_CODE } from '../constants';
import { Calendar, Tag, ArrowLeft, User, Share2, Copy, Check, Sparkles, Clock, ShieldCheck, Award } from 'lucide-react';

const BlogDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [copied, setCopied] = React.useState(false);
  
  const blog = BLOGS.find(b => b.id === id);

  if (!blog) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-24 text-center">
        <h2 className="text-3xl font-bold mb-4">Article Not Found</h2>
        <Link to="/blogs" className="text-blue-400 hover:underline">Back to all blogs</Link>
      </div>
    );
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(MAIN_COUPON_CODE);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-16 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Breadcrumbs for SEO */}
      <nav className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-gray-500 mb-8 overflow-x-auto whitespace-nowrap">
        <Link to="/" className="hover:text-white">Home</Link>
        <span className="text-gray-800">/</span>
        <Link to="/blogs" className="hover:text-white">Success Hub</Link>
        <span className="text-gray-800">/</span>
        <span className="text-blue-500">{blog.title.slice(0, 20)}...</span>
      </nav>

      <article className="bg-[#0f0f0f] border border-white/10 rounded-[50px] overflow-hidden shadow-2xl relative">
        {/* Prominent Header / Author Hero Section */}
        <div className="aspect-[21/9] bg-[#111] relative overflow-hidden">
          <img 
            src={`https://picsum.photos/seed/${blog.id}/1200/600`} 
            alt={blog.title} 
            loading="lazy"
            className="w-full h-full object-cover opacity-50 grayscale"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f0f] via-[#0f0f0f]/60 to-transparent" />
          
          <div className="absolute bottom-12 left-12 right-12 z-20">
             <div className="flex flex-wrap items-center gap-4 mb-8">
                {/* Category Badge - Highly Prominent */}
                <span className="bg-blue-600 px-6 py-2.5 rounded-full text-[11px] font-black uppercase tracking-[0.2em] shadow-[0_10px_30px_rgba(37,99,235,0.4)] border border-white/20 text-white">
                  {blog.category}
                </span>
                <span className="text-white/60 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 bg-black/40 backdrop-blur-md px-4 py-2 rounded-full border border-white/5">
                  <Clock size={14} className="text-blue-500" /> 5 MIN READ
                </span>
             </div>
             <h1 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tighter leading-none italic mb-4">{blog.title}</h1>
          </div>
        </div>

        <div className="p-10 md:p-16">
          {/* Author Badge - Redesigned for Maximum Prominence (E-E-A-T) */}
          <div className="flex flex-col sm:flex-row items-center justify-between mb-16 pb-12 border-b border-white/5 gap-8">
            <div className="flex items-center gap-6">
              <div className="relative group">
                <div className="absolute inset-0 bg-blue-600 rounded-[28px] blur-xl opacity-20 group-hover:opacity-40 transition-opacity"></div>
                <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-900 rounded-[28px] flex items-center justify-center border-2 border-white/10 relative z-10 shadow-2xl rotate-3 group-hover:rotate-0 transition-transform duration-500">
                  <User size={36} className="text-white" />
                  <div className="absolute -bottom-2 -right-2 bg-yellow-500 p-1.5 rounded-lg shadow-lg">
                    <Award size={14} className="text-black" />
                  </div>
                </div>
              </div>
              <div>
                <p className="text-white font-black uppercase text-2xl tracking-tighter italic">Jitesh Jana</p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-blue-400 text-[11px] font-black uppercase tracking-widest">Lead Student Ambassador</p>
                  <span className="w-1 h-1 bg-white/20 rounded-full"></span>
                  <p className="text-green-500 text-[11px] font-black uppercase tracking-widest flex items-center gap-1.5">
                    <ShieldCheck size={12} /> Verified
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col items-end">
               <div className="flex items-center gap-3 text-gray-500 text-[10px] font-black uppercase tracking-[0.3em] bg-white/5 px-6 py-3 rounded-2xl border border-white/5">
                 <Calendar size={14} className="text-blue-500" /> {blog.date}
               </div>
            </div>
          </div>

          {/* Article Content */}
          <div className="prose prose-invert prose-blue max-w-none text-gray-400 leading-relaxed text-lg font-medium italic mb-16">
            <div className="relative mb-16">
               <div className="absolute -left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-600 to-transparent"></div>
               <p className="text-white font-black text-2xl not-italic tracking-tight leading-tight pl-4">
                {blog.excerpt}
               </p>
            </div>
            
            <div className="space-y-8 text-white/80 font-medium not-italic">
              {blog.content?.split('\n').map((paragraph, idx) => {
                const text = paragraph.trim();
                if (!text) return null;
                return (
                  <p key={idx} className="leading-relaxed first-letter:text-5xl first-letter:font-black first-letter:text-blue-500 first-letter:mr-3 first-letter:float-left">
                    {text}
                  </p>
                );
              })}
            </div>
          </div>

          {/* Savings Call to Action */}
          <div className="p-10 md:p-16 bg-gradient-to-br from-[#111] to-[#0a0a0a] rounded-[50px] border border-blue-600/20 relative overflow-hidden group shadow-3xl mb-16">
            <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none group-hover:scale-110 transition-transform duration-1000">
              <Sparkles size={160} className="text-blue-500" />
            </div>
            
            <div className="relative z-10 flex flex-col items-center text-center">
              <h3 className="text-3xl md:text-5xl font-black text-white mb-6 uppercase tracking-tighter italic">Ready to start your 2026 prep?</h3>
              <p className="text-gray-500 font-bold text-sm uppercase tracking-[0.2em] mb-12">Apply Jitesh Jana's Referral for Instant Max Savings</p>
              
              <div className="w-full max-w-md bg-black/60 backdrop-blur-2xl rounded-[40px] p-10 border border-white/10 shadow-inner flex flex-col items-center group/card">
                <p className="text-[10px] text-white/30 font-black uppercase tracking-[0.4em] mb-6">LIVE AMBASSADOR CODE</p>
                <div className="flex items-center justify-between w-full bg-white/5 rounded-3xl p-6 border border-white/5">
                  <span className="text-3xl sm:text-4xl font-mono font-black text-blue-400 tracking-tighter">{MAIN_COUPON_CODE}</span>
                  <button 
                    onClick={handleCopy}
                    className={`p-4 rounded-2xl transition-all active:scale-95 shadow-2xl ${copied ? 'bg-green-500 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                  >
                    {copied ? <Check size={20} /> : <Copy size={20} />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Article Sharing */}
          <div className="mt-16 pt-10 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
               <button className="flex items-center gap-2 px-8 py-4 bg-white/5 rounded-2xl hover:bg-white/10 transition-all text-xs font-black uppercase tracking-widest text-gray-400 hover:text-white">
                 <Share2 size={16} /> Share Knowledge
               </button>
            </div>
            <p className="text-[10px] text-gray-800 font-black uppercase tracking-[0.3em] italic">Written by Jitesh Jana • PW Ambassador Team</p>
          </div>
        </div>
      </article>
      
      {/* Suggestions Section */}
      <div className="mt-24">
        <div className="flex items-center justify-between mb-12 border-b border-white/5 pb-6">
          <h3 className="text-2xl font-black text-white uppercase tracking-tighter italic">More from Success Hub</h3>
          <Link to="/blogs" className="text-[10px] font-black uppercase text-blue-500 tracking-widest hover:text-white transition-colors">View All Articles</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {BLOGS.filter(b => b.id !== blog.id).slice(0, 4).map(other => (
            <Link key={other.id} to={`/blogs/${other.id}`} className="p-8 bg-white/5 border border-white/10 rounded-[35px] hover:border-blue-500/40 hover:bg-white/10 transition-all flex items-center gap-6 group">
               <div className="w-24 h-24 rounded-2xl overflow-hidden bg-[#111] shrink-0 border border-white/5">
                  <img src={`https://picsum.photos/seed/${other.id}/200/200`} alt="" loading="lazy" className="w-full h-full object-cover opacity-40 grayscale group-hover:grayscale-0 group-hover:scale-110 transition-transform duration-700" />
               </div>
               <div>
                  <h4 className="font-black text-white group-hover:text-blue-400 transition-colors line-clamp-2 uppercase text-sm tracking-tight leading-tight">{other.title}</h4>
                  <div className="flex items-center gap-3 mt-3">
                    <span className="text-[8px] text-blue-500 font-black uppercase tracking-widest">{other.category}</span>
                    <span className="text-[8px] text-gray-700 font-black uppercase tracking-widest">{other.date}</span>
                  </div>
               </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BlogDetail;
